import { useCallback, useState } from 'react';

import {
  QRReadResult,
  readQRFromCanvas,
  readQRFromFile,
  readQRFromUrl,
} from '../utils/qrReader';

/**
 * Состояние хука для чтения QR-кодов
 */
interface QRReaderState {
  isLoading: boolean;
  result: QRReadResult | null;
  error: string | null;
}

/**
 * Хук для чтения QR-кодов из различных источников
 * @returns Объект с состоянием и методами для работы с QR-кодами
 */
export const useQRReader = () => {
  const [state, setState] = useState<QRReaderState>({
    isLoading: false,
    result: null,
    error: null,
  });

  /**
   * Сброс состояния
   */
  const reset = useCallback(() => {
    setState({
      isLoading: false,
      result: null,
      error: null,
    });
  }, []);

  /**
   * Чтение QR-кода из файла
   * @param file - Файл изображения с QR-кодом
   */
  const readFromFile = useCallback(async (file: File) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await readQRFromFile(file);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        result,
        error: result.success ? null : result.error || 'Неизвестная ошибка',
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка',
      }));
    }
  }, []);

  /**
   * Чтение QR-кода из URL изображения
   * @param imageUrl - URL изображения с QR-кодом
   */
  const readFromUrl = useCallback(async (imageUrl: string) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await readQRFromUrl(imageUrl);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        result,
        error: result.success ? null : result.error || 'Неизвестная ошибка',
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка',
      }));
    }
  }, []);

  /**
   * Чтение QR-кода из canvas элемента
   * @param canvas - Canvas элемент с изображением QR-кода
   */
  const readFromCanvas = useCallback(async (canvas: HTMLCanvasElement) => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const result = await readQRFromCanvas(canvas);
      setState((prev) => ({
        ...prev,
        isLoading: false,
        result,
        error: result.success ? null : result.error || 'Неизвестная ошибка',
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Неизвестная ошибка',
      }));
    }
  }, []);

  /**
   * Чтение QR-кода из input элемента (файл)
   * @param input - HTMLInputElement с типом file
   */
  const readFromInput = useCallback(
    async (input: HTMLInputElement) => {
      if (!input.files || input.files.length === 0) {
        setState((prev) => ({
          ...prev,
          error: 'Файл не выбран',
        }));
        return;
      }

      const file = input.files[0];
      await readFromFile(file);
    },
    [readFromFile],
  );

  return {
    // Состояние
    isLoading: state.isLoading,
    result: state.result,
    error: state.error,

    // Методы
    readFromFile,
    readFromUrl,
    readFromCanvas,
    readFromInput,
    reset,

    // Удобные геттеры
    isSuccess: state.result?.success || false,
    qrData: state.result?.data || null,
    hasError: !!state.error,
  };
};
